import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, estimateSuffix, defaultNumberFormat } from "../../components/index.ts";
import type { ResourceVerificationRow } from "./use-enforcement.ts";
import {
  deriveAssertionFailures,
  quantityMeasure,
  quantityValue,
  verificationPathLabel,
  assertionFailureHref,
} from "./vocab.ts";

/**
 * Assertion failures (task 11.8; design 12.2 lists "assertion failures" in the Enforcement row).
 *
 * CONTRACT NOTE (honest unknown): the `EnforcementSummary` schema has NO dedicated assertion field.
 * An assertion failure is a Resource Server verification whose error code is `invalid_assertion`, so
 * this panel surfaces exactly those `resource_verification` rows (via `deriveAssertionFailures`)
 * rather than inventing an API field. If the contract later adds a first-class assertion breakdown,
 * this panel is the one place to re-point.
 *
 * It is deliberately a small, prominent summary (not another full table): the per-path
 * `invalid_assertion` counts and their sum, each a drill to the verification events behind it. A
 * non-zero total is a warning the operator should see (icon + text via `StatusBadge`, never colour
 * alone); zero assertion failures is a neutral, healthy "none" state (design 12.3 / 12.4). A count
 * the server could not verify (null) is grey `unknown`, never green.
 */

export interface AssertionFailuresProps {
  readonly rows: readonly ResourceVerificationRow[];
}

export function AssertionFailures(props: AssertionFailuresProps): ReactNode {
  const navigate = useNavigate();
  const failures = deriveAssertionFailures(props.rows);

  // The total across paths; treats a null (no data) count as zero for the headline, but any null
  // present means the sum itself is not fully verifiable, so we mark the headline accordingly.
  const total = failures.reduce((sum, f) => sum + quantityValue(f.count), 0);
  const anyNull = failures.some((f) => f.count.value === null);
  const anyEstimate = failures.some((f) => f.count.exactness === "estimated");
  const headlineLevel = total > 0 ? "warn" : "neutral";
  const headlineMeasure = anyNull ? "unverifiable" : anyEstimate ? "estimate" : "exact";

  return (
    <section
      className="enf-assertions"
      data-active={total > 0 ? "true" : "false"}
      aria-label="Assertion failures"
    >
      <div className="enf-section__head">
        <h2 className="enf-section__title">Assertion failures</h2>
        <span className="enf-section__note">
          Gateway assertion verifications that failed (<code>invalid_assertion</code>).
        </span>
      </div>

      <p className="enf-assertions__headline">
        <StatusBadge
          level={headlineLevel}
          label={
            total > 0
              ? `${defaultNumberFormat(total)}${estimateSuffix(
                  headlineMeasure === "exact" ? undefined : headlineMeasure,
                )} assertion failure${total === 1 ? "" : "s"}`
              : "No assertion failures"
          }
          title={
            total > 0
              ? "One or more Gateway assertions failed verification in this window."
              : "No Gateway assertion verifications failed in this window."
          }
        />
      </p>

      {/* Per-path breakdown, each a drill. Both paths are shown only when the server reported them;
          assertion failures are not a catalog-complete breakdown, so an empty list reads as none. */}
      {failures.length === 0 ? (
        <p className="enf-faint">No assertion-failure verifications were recorded in this window.</p>
      ) : (
        <ul className="enf-assertions__list">
          {failures.map((f) => {
            const measure = quantityMeasure(f.count);
            const text =
              f.count.value === null
                ? "n/a"
                : `${defaultNumberFormat(f.count.value)}${estimateSuffix(measure)}`;
            return (
              <li key={f.path}>
                <span className="enf-faint">{verificationPathLabel(f.path)}</span>
                <button
                  type="button"
                  className="enf-link enf-num"
                  data-measure={measure}
                  onClick={() => navigate(assertionFailureHref(f.path))}
                  aria-label={`Open the assertion-failure events for ${verificationPathLabel(
                    f.path,
                  ).toLowerCase()}`}
                >
                  {text}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
